import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const EmployerSection = () => {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: "Users",
      title: t('employer.passiveTalent'),
      description: t('employer.passiveTalentDesc')
    },
    {
      icon: "Target",
      title: t('employer.structuredProfiles'),
      description: t('employer.structuredProfilesDesc')
    },
    {
      icon: "Zap",
      title: t('employer.fastMatching'),
      description: t('employer.fastMatchingDesc')
    },
    {
      icon: "Building2",
      title: t('employer.directHiring'),
      description: t('employer.directHiringDesc')
    }
  ];

  const comparisons = [
    {
      method: t('employer.headhunters'),
      icon: "UserX",
      problem: t('employer.headhuntersProblem'),
      solution: t('employer.headhuntersSolution')
    },
    {
      method: t('employer.jobBoards'),
      icon: "FileSearch",
      problem: t('employer.jobBoardsProblem'),
      solution: t('employer.jobBoardsSolution')
    },
    {
      method: t('employer.linkedinRecruiter'),
      icon: "MessageSquare",
      problem: t('employer.linkedinProblem'),
      solution: t('employer.linkedinSolution')
    }
  ];

  const candidatePreviews = [
    {
      title: "Chief Executive Officer",
      experience: "15+ years",
      skills: ["Strategic Planning", "P&L Management", "Board Relations", "M&A"],
      matchScore: 98
    },
    {
      title: "Chief Financial Officer",
      experience: "12+ years",
      skills: ["Financial Strategy", "IPO Experience", "Risk Management", "FP&A"],
      matchScore: 95
    },
    {
      title: "Chief Technology Officer",
      experience: "10+ years",
      skills: ["Cloud Architecture", "Team Leadership", "Product Strategy", "AI/ML"],
      matchScore: 92
    }
  ];

  return (
    <section className="py-20 md:py-32 lg:py-40 bg-gradient-to-br from-[#0025cc] via-[#0025cc] to-[#0025cc] text-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left side - Candidate Pool Preview */}
          <div className="order-2 lg:order-1 relative">
            <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-6 md:p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg md:text-xl font-semibold text-white">{t('employer.executiveCandidatePool')}</h3>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                  <Icon name="Users" size={16} color="#FFFFFF" />
                  <span className="text-sm font-medium text-white">247 {t('employer.matches')}</span>
                </div>
              </div>
              
              <div className="space-y-4">
                {candidatePreviews?.map((candidate, index) => (
                  <div 
                    key={index} 
                    className="bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0025cc] to-[#0025cc] flex items-center justify-center flex-shrink-0 shadow-md">
                        <Icon name="Briefcase" size={24} color="#FFFFFF" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-base md:text-lg font-bold text-[#111827]">{candidate?.title}</h4>
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-lg flex-shrink-0">
                            <Icon name="TrendingUp" size={14} color="#10B981" />
                            <span className="text-xs font-bold text-green-700">{candidate?.matchScore}%</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <Icon name="Award" size={14} color="#0025cc" />
                          <span className="text-sm font-medium text-[#0025cc]">{candidate?.experience} {t('employer.experience')}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {candidate?.skills?.map((skill, skillIndex) => (
                            <span 
                              key={skillIndex}
                              className="px-2.5 py-1 bg-[#0025cc]/10 text-[#0025cc] text-xs font-medium rounded-lg border border-[#0025cc]/20"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name="Shield" size={20} color="#FFFFFF" />
                    <span className="text-sm font-medium text-white">{t('employer.previewWhat')}</span>
                  </div>
                  <Icon name="Lock" size={18} color="#FFFFFF" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - Content */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4 md:mb-6 border border-white/30">
              <Icon name="Briefcase" size={20} color="#FFFFFF" />
              <span className="text-sm md:text-base font-medium text-white">{t('employer.badge')}</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight">
              {t('employer.title')}<br />{t('employer.titleLine2')}
            </h2>
            
            <p className="text-base md:text-lg text-white/90 mb-8 md:mb-10 leading-relaxed">
              {t('employer.subtitle')}
            </p>
            
            {/* Comparison Section with Glass Effect */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 mb-8 md:mb-10 shadow-xl">
              <h3 className="text-lg md:text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Icon name="TrendingUp" size={20} color="#FFFFFF" />
                {t('employer.whyBlinDBetter')}
              </h3>
              <div className="space-y-4">
                {comparisons?.map((comparison, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                          <Icon name={comparison?.icon} size={16} color="#FFFFFF" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white text-sm mb-1">{comparison?.method}</div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs md:text-sm">
                          <div className="flex items-center gap-1.5 text-red-300">
                            <Icon name="X" size={14} color="#FCA5A5" />
                            <span>{comparison?.problem}</span>
                          </div>
                          <div className="hidden sm:block text-white/50">→</div>
                          <div className="flex items-center gap-1.5 text-green-300">
                            <Icon name="Check" size={14} color="#86EFAC" />
                            <span className="font-medium">{comparison?.solution}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-4 md:space-y-6 mb-8 md:mb-10">
              {benefits?.map((benefit, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                      <Icon name={benefit?.icon} size={20} color="#FFFFFF" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-white mb-1">
                      {benefit?.title}
                    </h3>
                    <p className="text-sm md:text-base text-white/80">
                      {benefit?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            <Button 
              variant="default" 
              size="lg"
              onClick={() => window.location.href = '/access-request'}
              iconName="ArrowRight"
              iconPosition="right"
              className="bg-white text-[#0025cc] hover:bg-white/90 shadow-xl hover:shadow-2xl transition-all duration-300 w-full sm:w-auto"
            >
              {t('employer.requestEmployerAccess')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmployerSection;